import React from 'react';
import { Chip } from '@mui/material';
import { Source as SourceIcon, PersonAdd as ManualAddIcon, Business as BuildingIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';

/** Human-readable labels for roster source API enum values. */
const ROSTER_SOURCE_LABELS: Record<string, string> = {
  badgeData: 'Badged In',
  lobbyTrack: 'Lobby Track',
  assignedOffice: 'Assigned Office',
  manualAdd: 'Manual Add',
};

interface RosterSourceChipProps {
  rosterSource?: string | null;
  isUnlisted?: boolean;
  size?: 'small' | 'medium';
}

const RosterSourceChip: React.FC<RosterSourceChipProps> = ({ rosterSource, isUnlisted, size = 'small' }) => {
  // Manual Add takes precedence for unlisted persons
  if (isUnlisted) {
    return (
      <Chip
        label="Manual Add"
        size={size}
        icon={<ManualAddIcon sx={{ fontSize: 12 }} />}
        sx={{
          height: 18,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: appColors.status.warningBg,
          color: appColors.status.warning,
          '& .MuiChip-icon': { color: appColors.status.warning },
        }}
      />
    );
  }

  if (!rosterSource) return null;

  const label = ROSTER_SOURCE_LABELS[rosterSource] ?? rosterSource;
  const isManual = label === 'Manual Add';
  const isAssignedOffice = label === 'Assigned Office';
  const isBadgeData = label === 'Badged In';
  const isLobbyTrack = label === 'Lobby Track';

  const chipIcon = isManual
    ? <ManualAddIcon sx={{ fontSize: 12 }} />
    : isAssignedOffice
      ? <BuildingIcon sx={{ fontSize: 12 }} />
      : <SourceIcon sx={{ fontSize: 12 }} />;

  const chipColor = isManual
    ? { bg: appColors.status.warningBg, fg: appColors.status.warning }
    : isBadgeData
      ? { bg: appColors.status.successBg, fg: appColors.status.success }
      : isLobbyTrack
        ? { bg: '#E0F2F1', fg: appColors.primary.main }
        : { bg: '#ECEFF1', fg: '#546E7A' };

  return (
    <Chip
      label={label}
      size={size}
      icon={chipIcon}
      sx={{
        height: 18,
        fontSize: '0.65rem',
        fontWeight: 600,
        bgcolor: chipColor.bg,
        color: chipColor.fg,
        '& .MuiChip-icon': { color: chipColor.fg },
      }}
    />
  );
};

export default React.memo(RosterSourceChip);
